// User Report — lists every user/employee of the company with their login
// (username + password), mobile, department and assigned menus. Includes a
// department filter and a show/hide-passwords toggle.
import React, { useEffect, useMemo, useState } from 'react';
import config from '../../config';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';

export default function UserReport() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [dept, setDept] = useState('All');
  const [search, setSearch] = useState('');

  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');

  useEffect(() => {
    fetch(
      `${config.API_BASE_URL}/Home/GetAllUsers/${encodeURIComponent(company?.id)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        // de-dupe by id (GetAllUsers can return a row per menu join)
        setUsers([...new Map(list.map((u) => [u.id, u])).values()]);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const departments = useMemo(() => {
    const set = new Set(
      users.map((u) => (u.department || '').trim()).filter(Boolean),
    );
    return ['All', ...Array.from(set).sort()];
  }, [users]);

  const filtered = users.filter((u) => {
    const deptOk = dept === 'All' || (u.department || '') === dept;
    const q = search.trim().toLowerCase();
    const searchOk =
      !q ||
      [u.name, u.username, u.phone, u.department]
        .map((x) => (x || '').toLowerCase())
        .some((x) => x.includes(q));
    return deptOk && searchOk;
  });

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='60vh'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant='h4' fontWeight='bold' mb={3} textAlign='center'>
        🧾 User Report
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 2,
        }}>
        <TextField
          select
          size='small'
          label='Department'
          value={dept}
          onChange={(e) => setDept(e.target.value)}
          sx={{ minWidth: 180 }}>
          {departments.map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size='small'
          label='Search name / username / mobile'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showPwd}
              onChange={(e) => setShowPwd(e.target.checked)}
            />
          }
          label='Show passwords'
        />
        <Chip label={`${filtered.length} user(s)`} color='primary' />
      </Box>

      <Paper elevation={3}>
        <TableContainer sx={{ maxHeight: '72vh' }}>
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                {[
                  '#',
                  'Name',
                  'Username',
                  'Password',
                  'Mobile',
                  'Department',
                  'Menus',
                ].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 'bold' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((u, i) => (
                <TableRow key={u.id} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{u.name || '-'}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {showPwd ? u.password || '-' : '••••••'}
                  </TableCell>
                  <TableCell>{u.phone || '-'}</TableCell>
                  <TableCell>
                    {u.department ? (
                      <Chip size='small' label={u.department} />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 280,
                      whiteSpace: 'normal',
                      fontSize: 12,
                      color: 'text.secondary',
                    }}>
                    {u.menuKeys || '-'}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align='center'>
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
